import { DateTime } from "luxon";

import Filters from "../../../../filters";
import Account from "../../../../Account";
import { cents } from "../../../utils";
import { getStartDate } from "../../modules/achPayments/utils";

import { DisplayStatus } from "./consts";
import { type FormattedBulkPayment } from "./types";

import type { FormikErrors } from "formik";
import type { BasePaymentRowType } from "./bulkBase";
import type { BulkPaymentFormType } from "./BulkPayment.form";

type BulkSummaryPayment = Pick<
  API.FormattedPaymentActivity,
  "amount" | "isCollectPayment" | "isSendPayment"
>;

export const formatBulkAmountTotal = (amount: number): string => {
  return Filters.currency(amount as Cents, {
    hasDecimal: false,
  });
};

export const formatBulkNetTotal = (amount: number): string => {
  const formattedAmount = formatBulkAmountTotal(amount);

  return amount > 0 ? `+${formattedAmount}` : formattedAmount;
};

export const summarizeBulkPayments = (payments: BulkSummaryPayment[]) => {
  return payments.reduce(
    (summary, payment) => {
      let {
        sendingCount,
        sendingTotal,
        collectingCount,
        collectingTotal,
        netTotal,
      } = summary;

      if (payment.isSendPayment) {
        sendingCount += 1;
        sendingTotal += payment.amount;
        netTotal -= payment.amount;
      }

      if (payment.isCollectPayment) {
        collectingCount += 1;
        collectingTotal += payment.amount;
        netTotal += payment.amount;
      }

      return {
        sendingCount,
        sendingTotal,
        collectingCount,
        collectingTotal,
        netTotal,
      };
    },
    {
      sendingCount: 0,
      sendingTotal: 0,
      collectingCount: 0,
      collectingTotal: 0,
      netTotal: 0,
    },
  );
};

export const calculateBulkTotals = (payments: BasePaymentRowType[]) => {
  let collectTotalCents = 0;
  let sendTotalCents = 0;

  payments.forEach((payment) => {
    const amount = payment.amount ?? 0;

    if (payment.transactionType === "collect") {
      collectTotalCents += amount;
    } else if (payment.transactionType === "send") {
      sendTotalCents += amount;
    }
  });

  const netTotalCents = collectTotalCents - sendTotalCents;

  let sign = "";
  if (netTotalCents > 0) {
    sign = "+";
  } else if (netTotalCents < 0) {
    sign = "-";
  }

  return {
    collectTotalFormatted: Filters.currency(cents(collectTotalCents)),
    sendTotalFormatted: Filters.currency(cents(sendTotalCents)),
    netTotalFormatted: Filters.currency(cents(Math.abs(netTotalCents))),
    netTotalFormattedWithSign: `${sign}${Filters.currency(
      cents(Math.abs(netTotalCents)),
    )}`,
  };
};

const getDateRangeDescription = (
  dates: (string | null)[],
  dualApprovalRequired: boolean,
) => {
  if (!dates || dates.length === 0) {
    return {
      short: "",
      long: "",
    };
  }

  const parsedDates = dates
    .map((d) => (d ? DateTime.fromISO(d) : null))
    .filter((d): d is DateTime => d !== null);

  const hasNull = dates.includes(null);

  // dual approval required, all null dates -> show "Upon approval"
  if (dualApprovalRequired && parsedDates.length === 0) {
    return {
      short: "Upon approval",
      long: "Upon approval",
    };
  }

  const firstDate = parsedDates[0];
  const lastDate = parsedDates[parsedDates.length - 1];
  const formatShortMonthFullyear = (dt: DateTime) =>
    dt?.toFormat("MMM d, yyyy");
  const formatFullMonthFullYear = (dt: DateTime) =>
    dt?.toFormat("MMMM d, yyyy");
  const allSameDate = parsedDates.every((d) => d.hasSame(firstDate, "day"));
  const formattedShortFirst = formatShortMonthFullyear(firstDate);
  const formattedShortLast = formatShortMonthFullyear(lastDate);
  const formattedLongFirst = formatFullMonthFullYear(firstDate);
  const formattedLongLast = formatFullMonthFullYear(lastDate);

  // dual approval required, contains null -> show "Upon approval - last date"
  if (dualApprovalRequired && hasNull) {
    const displayDate = allSameDate ? formattedShortFirst : formattedShortLast;
    const displayLongDate = allSameDate
      ? formattedLongFirst
      : formattedLongLast;
    return {
      short: `Upon approval - ${displayDate}`,
      long: `Upon approval - ${displayLongDate}`,
    };
  }

  // all same date -> show single date
  if (allSameDate) {
    return {
      short: formattedShortFirst,
      long: formattedLongFirst,
    };
  }

  // different dates -> show first and last date range
  return {
    short: `${firstDate.toFormat("MMM d")} - ${formattedShortLast}`,
    long: `${formattedLongFirst} - ${formattedLongLast}`,
  };
};

export const formatBulkPayment = (
  bulkPayment: API.BulkPayment,
  accounts: API.AnyAccount[],
): FormattedBulkPayment => {
  const institutionAccount = accounts.find(
    (a) => a.id === bulkPayment.institution_account,
  );

  const {
    id: uuid,
    type,
    reversal_of: reversalOf,
    title,
    description,
    template_version: templateVersion,
    state,
    payment_count: paymentCount,
    payment_total_amount: paymentTotalAmount,
    recipient_count: recipientCount,
    created_at: createdAt,
    updated_at: updatedAt,
    sec_code: secCode,
    group_posting: groupPosting,
    entry_desc: entryDesc,
    company_name: companyName,
    company_id: companyId,
    dual_approval_id: dualApprovalId,
    scheduled_dates: scheduledDates,
    is_expedited: isExpedited,
    payment_credit_count: paymentCreditCount,
    payment_debit_count: paymentDebitCount,
    payment_total_credit_amount: paymentTotalCreditAmount,
    payment_total_debit_amount: paymentTotalDebitAmount,
    organization_file: organizationFile,
    bulk_reversals: bulkReversals,
    is_reversible: isReversible,
    reversal_reason: reversalReason,
  } = bulkPayment;

  const bulkPaymentState = state ?? "unknown";
  const displayStatus =
    DisplayStatus[bulkPaymentState as keyof typeof DisplayStatus] ||
    DisplayStatus.unknown;

  // calculate net total: debit (collect) - credit (send)
  const netTotal = paymentTotalDebitAmount - paymentTotalCreditAmount;
  const netTotalDollarsWithSign = formatBulkNetTotal(netTotal);
  // convert credit amount and debit amount from cents to dollars for display
  const creditTotalDollars = formatBulkAmountTotal(paymentTotalCreditAmount);
  const debitTotalDollars = formatBulkAmountTotal(paymentTotalDebitAmount);

  const formattedAccount = institutionAccount
    ? Account.deserialize(institutionAccount)
    : null;

  const formattedDateRange = getDateRangeDescription(
    scheduledDates,
    Boolean(dualApprovalId),
  );

  const { short: dateRange, long: longDateRange } = formattedDateRange;

  return {
    uuid,
    type,
    reversalOf,
    title,
    description,
    templateVersion,
    state: bulkPaymentState,
    displayStatus,
    paymentCount,
    paymentTotalAmount,
    recipientCount,
    netTotal,
    netTotalDollarsWithSign,
    institutionAccount: formattedAccount,
    createdAt,
    updatedAt,
    secCode,
    groupPosting,
    entryDesc,
    companyName,
    companyId,
    dualApprovalId,
    dateRange,
    longDateRange,
    hasReversal: bulkReversals.length > 0,
    isExpedited,
    paymentCreditCount,
    paymentDebitCount,
    paymentTotalCreditAmount,
    paymentTotalDebitAmount,
    creditTotalDollars,
    debitTotalDollars,
    organizationFile,
    isReversible,
    isReversal: type === "bulk_reversal",
    reversalReason,
  } as FormattedBulkPayment;
};

export const showCancelOption = (bulkPayment: FormattedBulkPayment) => {
  const { displayStatus } = bulkPayment;

  return (
    displayStatus === DisplayStatus.pending_approval ||
    displayStatus === DisplayStatus.in_progress ||
    displayStatus === DisplayStatus.scheduled
  );
};

// Whether the user has permission to reverse at least one payment direction
// present in the group. The extra gating should be done in the multi-select screen
export const hasAnyReversePermission = (
  {
    hasCollectPayments,
    hasSendPayments,
  }: { hasCollectPayments: boolean; hasSendPayments: boolean },
  {
    hasReverseCollectPermission,
    hasReverseSendPermission,
  }: {
    hasReverseCollectPermission: boolean;
    hasReverseSendPermission: boolean;
  },
) =>
  (hasCollectPayments && hasReverseCollectPermission) ||
  (hasSendPayments && hasReverseSendPermission);

export const showReverseOption = (
  payment: FormattedBulkPayment | { isReversible?: boolean } | null,
  {
    isReversalFeatureEnabled,
    hasReversePermission,
  }: { isReversalFeatureEnabled: boolean; hasReversePermission: boolean },
) =>
  Boolean(
    isReversalFeatureEnabled && payment?.isReversible && hasReversePermission,
  );

// achNextCutoff is Unix timestamp (in seconds)
export const getBulkPaymentStartDate = (
  expeditedPaymentsFeature?: boolean,
  achNextCutoff?: number,
  canSendInstantPayments?: boolean,
) => {
  // if expedited payments feature is enabled, we allow user to select a date that is past the ACH cutoff date
  if (canSendInstantPayments && expeditedPaymentsFeature) {
    return DateTime.now().toFormat("MM/dd/yyyy");
  }

  const achNextCutoffDate = getStartDate(achNextCutoff);
  return achNextCutoffDate;
};

export const getScreenWithError = (errors: any) => {
  const DEFAULT_ERROR_MESSAGE =
    "We could not process your bulk payment. Please try again.";

  if (!errors || typeof errors !== "object") {
    return {
      screen: 1,
      message: DEFAULT_ERROR_MESSAGE,
    };
  }

  const fieldsOnFirstScreen = [
    "transaction_type",
    "institution_account",
    "company",
    "title",
    "sec_code",
    "entry_desc",
  ];

  const fieldsOnSecondScreen = ["recipient", "expedite_choice", "amount"];

  const findFieldError = (
    errorsResponse: any,
    fields: string[],
    screen: number,
  ) => {
    const field = fields.find((f) => errorsResponse?.[f]?.length);
    return field ? { screen, message: errorsResponse[field][0] } : null;
  };

  // top-level first screen
  const firstScreenTop = findFieldError(errors, fieldsOnFirstScreen, 1);
  if (firstScreenTop) return firstScreenTop;

  // top-level second screen
  const secondScreenTop = findFieldError(errors, fieldsOnSecondScreen, 2);
  if (secondScreenTop) return secondScreenTop;

  // nested payments
  const payments = errors.message?.payments;
  if (Array.isArray(payments)) {
    const paymentError = payments
      .map(
        (payment) =>
          findFieldError(payment, fieldsOnFirstScreen, 1) ||
          findFieldError(payment, fieldsOnSecondScreen, 2),
      )
      .find(Boolean);

    if (paymentError) return paymentError;
  }

  // fallback to non_field_errors or string message and default to screen 1
  const fallbackMessage =
    errors.message?.non_field_errors?.[0] ||
    (typeof errors.message === "string"
      ? errors.message
      : DEFAULT_ERROR_MESSAGE);

  return { screen: 1, message: fallbackMessage };
};

type FormErrors = FormikErrors<BulkPaymentFormType>;

// filters out validation errors for excluded rows
// used in template mode when creating bulk payments
export const filterExcludedRowErrors = (
  errors: FormErrors,
  excludedRows: Set<number>,
): FormErrors => {
  const paymentErrors = errors.payments;
  if (!paymentErrors || !Array.isArray(paymentErrors)) {
    return errors;
  }

  const filteredErrors = paymentErrors.map((err, i) =>
    excludedRows.has(i) ? undefined : err,
  );
  return { ...errors, payments: filteredErrors } as FormErrors;
};

// checks if there are any validation errors remaining after filtering
export const hasValidationErrors = (errors: FormErrors): boolean => {
  const paymentErrors = errors.payments;
  const otherErrors = Object.keys(errors).filter((k) => k !== "payments");

  if (otherErrors.length > 0) {
    return true;
  }

  // Formik represents array-level validation errors (e.g. from Yup .min())
  // as a string on the field key rather than an array
  if (typeof paymentErrors === "string") {
    return true;
  }

  if (!Array.isArray(paymentErrors)) {
    return false;
  }

  return paymentErrors.some(Boolean);
};
